
import { useState, useEffect } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import Select from "react-select"
import { Button } from "react-bootstrap"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import Swal from "sweetalert2"
import { useDispatch } from "react-redux"
import { createKarmaAction, updateKarmaAction } from "../../../store/actions/KarmaAction"
import { handleSuccess, handleError, showErrorAlert } from "../../../services/ErrorHandlingService"

const AddKarma = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const dispatch = useDispatch()

  const isEditMode = Boolean(params.id)
  const karmaToEdit = location.state?.karma

  const [formData, setFormData] = useState({
    name: "",
    instruction: "",
    description: "",
    depends_on: "",
    karma: "",
    duration_in_days: "",
    charges: "",
    benefits: "",
    precautions: "",
    karma_status: "Active",
  })
  const [todos, setTodos] = useState([])
  const [newTodo, setNewTodo] = useState("")
  const [fieldDefinitions, setFieldDefinitions] = useState([])
  const [newFieldDefinition, setNewFieldDefinition] = useState("")
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ]

  useEffect(() => {
    if (isEditMode && karmaToEdit) {
      setFormData({
        id: karmaToEdit.karma_id ?? karmaToEdit.id,
        name: karmaToEdit.karma_name || "",
        instruction: karmaToEdit.karma_instructions || "",
        description: karmaToEdit.karma_description || "",
        depends_on: karmaToEdit.karma_depends_on || "",
        duration_in_days: (karmaToEdit.karma_duration_in_days ?? "").toString(),
        charges: karmaToEdit.karma_charges || "",
        benefits: karmaToEdit.karma_benefits || "",
        precautions: karmaToEdit.karma_precautions || "",
        karma_status: karmaToEdit.karma_status || "Active",
      })
      setTodos(
        Array.isArray(karmaToEdit.todos)
          ? karmaToEdit.todos
          : typeof karmaToEdit.todos === "string"
          ? JSON.parse(karmaToEdit.todos || "[]")
          : []
      )
    }
  }, [isEditMode, karmaToEdit])

  
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }))
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = "Name is required"
    if (!formData.instruction.trim()) newErrors.instruction = "Instruction is required"
    if (!formData.duration_in_days.trim()) newErrors.duration_in_days = "Duration is required"
    if (
      formData.duration_in_days &&
      (isNaN(formData.duration_in_days) || Number.parseInt(formData.duration_in_days) <= 0)
    )
      newErrors.duration_in_days = "Duration must be a positive number"
    if (!formData.charges.trim()) newErrors.charges = "Charges information is required"
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      if (isEditMode) {
        const payload = {
          karma_id: formData.id,
          karma_name: formData.name,
          karma_instructions: formData.instruction,
          description: formData.description,
          depends_on: formData.depends_on,
          duration_in_days: formData.duration_in_days,
          charges: formData.charges,
          benefits: formData.benefits,
          precautions: formData.precautions,
          karma_status: formData.karma_status,
          todos: JSON.stringify(todos)

        }
        const res = await dispatch(updateKarmaAction(payload))
        if (res?.success) {
          handleSuccess("Karma updated successfully"); 
          // Swal.fire({
          //   icon: "success",
          //   title: "Updated!",
          //   text: "Karma updated successfully",
          //   timer: 1400,
          //   showConfirmButton: false,
          // })
          goBack()
          return
        }
        throw new Error(res?.error || "Update failed")
      } else {
        const payload = {
          karma_name: formData.name,
          karma_instructions: formData.instruction,
          description: formData.description,
          depends_on: formData.depends_on,
          duration_in_days: formData.duration_in_days,
          charges: formData.charges,
          benefits: formData.benefits,
          precautions: formData.precautions,
          karma_status: formData.karma_status,
          todos: JSON.stringify(todos)

        }
        const res = await dispatch(createKarmaAction(payload))
        if (res?.success) {
          // Swal.fire({
          //   icon: "success",
          //   title: "Created!",
          //   text: "Karma created successfully",
          //   timer: 1400,
          //   showConfirmButton: false,
          // })
         
          handleSuccess("Karma created successfully");
          goBack()
          return
        }
        throw new Error(res?.error || "Create failed")
      }
    } catch (error) {
      console.error("Save karma error:", error)
      Swal.fire({ icon: "error", title: "Error", text: error.message || "Failed to save karma" })
    } finally {
      setIsSubmitting(false)
    }
  }

  const goBack = () => {
    const returnTo = location.state?.returnTo
    if (returnTo) {
      navigate("/karma", {
        state: {
          currentPage: returnTo.currentPage,
          sortField: returnTo.sortField,
          sortOrder: returnTo.sortOrder,
        },
      })
    } else {
      navigate("/karma")
    }
  }

  const handleAddTodo = () => {
    if (!newTodo.trim()) return
    setTodos((prev) => [...prev, { todo_id: "0", todo: newTodo.trim() }])
    setNewTodo("")
  }


  const handleDeleteTodo = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This to-do will be removed from the list.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ffffff", // white background
      cancelButtonColor: "#ff0000",  // red background
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      didOpen: () => {
        // 🔧 force visible text color when button is white
        const confirmBtn = Swal.getConfirmButton();
        const cancelBtn = Swal.getCancelButton();
        if (confirmBtn) {
          confirmBtn.style.color = "#000"; // black text for white button
          confirmBtn.style.border = "1px solid #ccc";
        }
        if (cancelBtn) {
          cancelBtn.style.color = "#fff"; // white text for red button
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setTodos((prev) => prev.filter((_, i) => i !== index));
  
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The to-do has been removed successfully.",
          confirmButtonColor: "#ffffff",
          didOpen: () => {
            const confirmBtn = Swal.getConfirmButton();
            if (confirmBtn) {
              confirmBtn.style.color = "#000";
              confirmBtn.style.border = "1px solid #ccc";
            }
          },
        });
      }
    });
  };
  
  

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="d-flex justify-content-end mb-3">
          <Button variant="primary" onClick={goBack}>
            Back
          </Button>
        </div>

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter name here"
                  />
                  {errors.name &&< small className="text-danger">{errors.name}</small>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Depends On</label>
                  <Select
                    id="depends_on"
                    name="depends_on"
                    isMulti
                    options={[
                      { value: "Vamana", label: "Vamana" },
                      { value: "Virechana", label: "Virechana" },
                      { value: "Basti", label: "Basti" },
                      { value: "Nasya", label: "Nasya" },
                      { value: "Raktamokshana", label: "Raktamokshana" },
                    ]}
                    value={
                      Array.isArray(formData.depends_on)
                        ? formData.depends_on
                        : formData.depends_on
                          ? formData.depends_on.split(",").map((v) => ({ value: v.trim(), label: v.trim() }))
                          : []
                    }
                    onChange={(selected) => {
                      handleInputChange({
                        target: {
                          name: "depends_on",
                          value: selected.map((s) => s.value).join(","), 
                        },
                      })
                    }}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        backgroundColor: "#e0f0ff",
                      }),
                      multiValueLabel: (provided) => ({
                        ...provided,
                        color: "#007bff",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                    classNamePrefix="react-select"
                    className={errors.depends_on ? "is-invalid" : ""}
                    placeholder="Select dependencies"
                  />
                  {errors.depends_on && <div className="invalid-feedback">{errors.depends_on}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Duration (in days) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    name="duration_in_days"
                    value={formData.duration_in_days}
                    onChange={handleInputChange}
                    min="1"
                    className="form-control"
                    placeholder="Enter duration in days"
                  />
                  {errors.duration_in_days && <small className="text-danger">{errors.duration_in_days}</small>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Charges <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="charges"
                    value={formData.charges}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="e.g., Free, 100, 200"
                  />
                  {errors.charges && <small className="text-danger">{errors.charges}</small>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Status</label>
                  <Select
                    name="karma_status"
                    options={statusOptions}
                    value={statusOptions.find((opt) => opt.value === formData.karma_status)}
                    onChange={(opt) => setFormData((p) => ({ ...p, karma_status: opt.value }))}
                    isSearchable
                    classNamePrefix="select"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        backgroundColor: "#e0f0ff",
                      }),
                      multiValueLabel: (provided) => ({
                        ...provided,
                        color: "#007bff",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                  />
                </div>
                {/* Simple To-dos block (kept from your UI) */}

                <div className="col-md-12 mb-3">
                  <div className="card shadow-none">
                    <div className="card-body" style={{ marginLeft: "-2rem" }}>
                      <div className="d-flex align-items-end gap-2 mb-3">
                        <div className="flex-grow-1">
                          <label className="form-label">Enter To-dos</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter todo"
                            value={newTodo}
                            onChange={(e) => setNewTodo(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                handleAddTodo()
                              }
                            }}
                          />
                        </div>
                        <div>
                          <Button
                            variant="outline-primary"
                            type="button"
                            onClick={handleAddTodo}
                            disabled={!newTodo.trim()}
                          >
                            Add To-do
                          </Button>
                        </div>
                      </div>

                      {todos.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: "80%" }}>To-do</th>
                                <th style={{ width: "20%" }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {todos.map((t, index) => (
                                <tr key={index}>
                                  <td>{t.todo}</td>
                                  <td className="text-center">
                                    <span
                                      className="text-danger"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => handleDeleteTodo(index)}
                                      title="Delete Todo"
                                    >
                                      <i className="fa fa-trash fs-5" />
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
            


                <div className="col-12 mb-3">
                  <label className="form-label">
                    Instruction <span className="text-danger">*</span>
                  </label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.instruction}
                    onChange={(event, editor) => {
                      const data = editor.getData()
                      handleInputChange({ target: { name: "instruction", value: data } })
                    }}
                  />
                  {errors.instruction && <div className="invalid-feedback d-block">{errors.instruction}</div>}
                </div>

                <div className="col-12 mb-3">
                  <label className="form-label">Description</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.description}
                    onChange={(event, editor) => {
                      const data = editor.getData()
                      handleInputChange({ target: { name: "description", value: data } })
                    }}
                  />
                </div>

                <div className="col-12 mb-3">
                  <label className="form-label">Benefits</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.benefits}
                    onChange={(event, editor) => {
                      const data = editor.getData()
                      handleInputChange({ target: { name: "benefits", value: data } })
                    }}
                  />
                </div>

                <div className="col-12 mb-3">
                  <label className="form-label">Precautions</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.precautions}
                    onChange={(event, editor) => {
                      const data = editor.getData()
                      handleInputChange({ target: { name: "precautions", value: data } })
                    }}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-start gap-2 mt-4 pt-3 border-top">
                <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                  {isEditMode
                    ? isSubmitting
                      ? "Updating..."
                      : "Update Karma"
                    : isSubmitting
                      ? "Adding..."
                      : "Add Karma"}
                </button>
                <button type="button" onClick={goBack} className="btn btn-info" disabled={isSubmitting}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddKarma
